import React, { createContext, useCallback, useMemo, useContext } from 'react';
import { Switch } from '@antimatterhq/ui/components/switch';
import { Button } from '@antimatterhq/ui/components/button';
import Tooltip from '@antimatterhq/ui/components/tooltip';
import Table from '@antimatterhq/ui/components/table';
import {
  CustomCellProps,
  useTableRows,
} from '@antimatterhq/ui/hooks/table/useTableRows';
import { useTableColumns } from '@antimatterhq/ui/hooks/table/useTableColumns';
import { FieldSpecTableRow } from './FieldSpecTableData';
import { fieldSpecColumnDefinitions } from './fieldSpecColumnDefinitions';
import { DataSourceFieldSchemaValues } from '@/zod/schemas/dataSource/DataSourceField';
import { exhaustiveMatchingGuard } from '@/utils/util';
import { FieldSpecSchemaValues } from '@/zod/schemas/dataSource/Common';
import { Datasource_FieldSpec } from '@/types/api';

type FieldSpecTableActionHandlers = {
  onToggle?: (field: Datasource_FieldSpec, enabled: boolean) => void;
  onDelete: (field: FieldSpecSchemaValues) => void;
  onEdit: (source: {
    preset?: Datasource_FieldSpec;
    custom?: FieldSpecSchemaValues;
  }) => void;
  onRevert?: (source: {
    preset: Datasource_FieldSpec;
    custom: FieldSpecSchemaValues;
  }) => void;
  onReorderUp?: (row: FieldSpecTableRow) => void;
  onReorderDown?: (row: FieldSpecTableRow) => void;
  isFirstRow: (itemId: string) => boolean;
  isLastRow: (itemId: string) => boolean;
};

const FieldSpecTableActionHandlersContext = createContext<{
  onDelete: FieldSpecTableActionHandlers['onDelete'];
  onRevert: FieldSpecTableActionHandlers['onRevert'];
  onToggle: FieldSpecTableActionHandlers['onToggle'];
  onReorderUp: FieldSpecTableActionHandlers['onReorderUp'];
  onReorderDown: FieldSpecTableActionHandlers['onReorderDown'];
  onEdit: FieldSpecTableActionHandlers['onEdit'];
  isFirstRow: FieldSpecTableActionHandlers['isFirstRow'];
  isLastRow: FieldSpecTableActionHandlers['isLastRow'];
  disabled?: boolean;
}>({
  onDelete: () => {},
  onRevert: () => {},
  onToggle: () => {},
  onReorderUp: () => {},
  onReorderDown: () => {},
  onEdit: () => {},
  isFirstRow: () => false,
  isLastRow: () => false,
});

export const useFieldSpecTableActionHandlers = () =>
  useContext(FieldSpecTableActionHandlersContext);

type FieldSpecTableProps = {
  data: FieldSpecTableRow[];
  disabled?: boolean;
  'data-testid'?: string;
} & Omit<FieldSpecTableActionHandlers, 'isFirstRow' | 'isLastRow'>;

type FieldSpecTableContentProps = {
  data: FieldSpecTableRow[];
  'data-testid'?: string;
};

const getType = (type: DataSourceFieldSchemaValues['type']) => {
  switch (type) {
    case 'sql':
      return 'SQL';
    case 'literal':
      return 'Literal';
    case 'alias':
      return 'Alias';
    case 'source':
      return 'Copy';
    case 'join':
      return 'Join';
    default:
      exhaustiveMatchingGuard(type);
  }
};

const ActionCell = React.memo(({ item }: { item: FieldSpecTableRow }) => {
  const { onDelete, onRevert, onToggle, disabled } =
    useFieldSpecTableActionHandlers();

  const renderDeleteButton = useMemo(() => {
    return !!item.source.custom && !item.source.preset;
  }, [item.source.custom, item.source.preset]);

  const renderRevertButton = useMemo(() => {
    return !!item.source.custom && !!item.source.preset && onRevert;
  }, [item.source.custom, item.source.preset, onRevert]);

  const renderToggleButton = useMemo(() => {
    return !item.source.custom && !!item.source.preset && onToggle;
  }, [item.source.custom, item.source.preset, onToggle]);

  return (
    <>
      {renderDeleteButton && (
        <Button
          aria-label="Delete"
          iconOnly
          leftIcon="antimatterhq:trash"
          onClick={() => onDelete(item.source.custom!)}
          variant="ghost"
          className="p-0"
          size="small"
          disabled={disabled}
          data-testid={`field-delete-button-${item.id}`}
        />
      )}

      {renderRevertButton && (
        <Tooltip content="Revert to preset" side="left">
          <Button
            aria-label="Revert"
            iconOnly
            leftIcon="fa:undo"
            onClick={() =>
              onRevert?.({
                preset: item.source.preset!,
                custom: item.source.custom!,
              })
            }
            variant="ghost"
            className="p-0"
            size="small"
            disabled={disabled}
            data-testid={`field-revert-button-${item.id}`}
          />
        </Tooltip>
      )}

      {renderToggleButton && (
        <Switch
          checked={item.enabled}
          onCheckedChange={(checked) =>
            onToggle?.(item.source.preset!, checked)
          }
          disabled={disabled}
          data-testid={`field-toggle-${item.id}`}
        />
      )}
    </>
  );
});

ActionCell.displayName = 'ActionCell';

const EditCell = React.memo(({ item }: { item: FieldSpecTableRow }) => {
  const {
    onReorderUp,
    onReorderDown,
    onEdit,
    disabled,
    isFirstRow,
    isLastRow,
  } = useFieldSpecTableActionHandlers();

  return (
    <div className="invisible flex flex-row items-center justify-end gap-1">
      {onReorderUp && onReorderDown && (
        <div className="-ml-3 flex flex-col">
          <Button
            aria-label="Move Up"
            iconOnly
            leftIcon="lucide:chevron-up"
            onClick={() => onReorderUp(item)}
            variant="ghost"
            className="h-3 p-0"
            size="small"
            disabled={disabled || isFirstRow(item.id)}
            data-testid={`field-move-up-button-${item.id}`}
          />
          <Button
            aria-label="Move Down"
            iconOnly
            leftIcon="lucide:chevron-down"
            onClick={() => onReorderDown(item)}
            variant="ghost"
            className="h-3 p-0"
            size="small"
            disabled={disabled || isLastRow(item.id)}
            data-testid={`field-move-down-button-${item.id}`}
          />
        </div>
      )}
      <Button
        aria-label="Edit"
        iconOnly
        leftIcon="antimatterhq:pencil"
        onClick={() => onEdit(item.source)}
        variant="ghost"
        className="p-0"
        size="small"
        disabled={disabled}
        data-testid={`field-edit-button-${item.id}`}
      />
    </div>
  );
});

EditCell.displayName = 'EditCell';

function FieldSpecTableContent({
  data,
  'data-testid': dataTestId = 'field-spec-table',
}: FieldSpecTableContentProps) {
  const { columns, columnWidths, columnVisibility } = useTableColumns();

  const renderTypeCell = useCallback(
    ({ item }: { item: FieldSpecTableRow }) => getType(item.type),
    [],
  );

  const renderActionCell = useCallback(
    ({ item }: { item: FieldSpecTableRow }) => <ActionCell item={item} />,
    [],
  );

  const renderEditCell = useCallback(
    ({ item }: { item: FieldSpecTableRow }) => <EditCell item={item} />,
    [],
  );

  const customBodyCells: Record<
    string,
    React.ReactNode | React.ComponentType<CustomCellProps<FieldSpecTableRow>>
  > = useMemo(
    () => ({
      action: renderActionCell,
      type: renderTypeCell,
      edit: renderEditCell,
    }),
    [renderActionCell, renderEditCell, renderTypeCell],
  );

  const emptyState = useMemo(
    () => ({
      EmptyTableComponent: () => <></>,
    }),
    [],
  );

  const { bodyRows, headerRow } = useTableRows<FieldSpecTableRow>({
    items: data,
    columns,
    isLoading: false,
    pageSize: 10,
    'data-testid': dataTestId,
    emptyState,
    customBodyCells,
  });

  return (
    <Table.ResponsiveContainer>
      <Table.StyleProvider
        virtualized={false}
        columnWidths={columnWidths}
        columnVisibility={columnVisibility}
        pageSize={data.length}
        data-testid="field-spec-table-style-provider"
      >
        <Table.Table data-testid="field-spec-table">
          <Table.Header data-testid="field-spec-table-header">
            {headerRow}
          </Table.Header>
          <Table.Body data-testid="detection-rules-table-body">
            {bodyRows}
          </Table.Body>
        </Table.Table>
      </Table.StyleProvider>
    </Table.ResponsiveContainer>
  );
}

export function FieldSpecTable(props: FieldSpecTableProps) {
  const isFirstRow = useCallback(
    (itemId: string) => {
      return props.data.findIndex((row) => row.id === itemId) === 0;
    },
    [props.data],
  );

  const isLastRow = useCallback(
    (itemId: string) => {
      return (
        props.data.findIndex((row) => row.id === itemId) ===
        props.data.length - 1
      );
    },
    [props.data],
  );

  return (
    <FieldSpecTableActionHandlersContext.Provider
      value={{
        onDelete: props.onDelete,
        onRevert: props.onRevert,
        onToggle: props.onToggle,
        disabled: props.disabled,
        onReorderUp: props.onReorderUp,
        onReorderDown: props.onReorderDown,
        onEdit: props.onEdit,
        isFirstRow,
        isLastRow,
      }}
    >
      <Table.Provider
        columnDefinitions={fieldSpecColumnDefinitions}
        localStorageKeyPrefix="field-spec-table"
      >
        <FieldSpecTableContent {...props} />
      </Table.Provider>
    </FieldSpecTableActionHandlersContext.Provider>
  );
}
